import { Button } from '@ipfabric/ui-components/Button/Button';
import { IconButton } from '@ipfabric/ui-components/IconButton/IconButton';
import { Switch } from '@ipfabric/ui-components/Switch/Switch';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useApiTableDeletionHelper } from 'components/ApiTable/helpers/useApiTableDeletionHelper';
import { reloadTableData } from 'components/ApiTable/model/commands/reloadTableData';
import { ApiTablePageView } from 'components/ApiTablePage/ApiTablePageView';
import { useApiTablePage } from 'components/ApiTablePage/useApiTablePage';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import { Dialog } from 'components/Dialog/Dialog';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type {
  TableActionsItem,
  TableBulkAction,
} from 'components/Table/model/TableModel';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AppTranslations } from 'translations/translations';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApiScopeRequirementMet } from 'utils/permissions/useApiScopeRequirementMet';
import { useApi } from 'utils/request/useApi';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import { TestDialog } from './TestDialog';
import type {
  EventType,
  WebHookId,
  WebHookRowData,
  WebHookType,
  WebhookTrigger,
} from './WebHooksState';
import type { TestWebHookCommand } from './commands/testWebHook';
import { WEBHOOKS_BASE_URL } from './constants';
import { useTypeTranslator } from './helpers/useTypeTranslator';

// Maximum length of the event-triggers cell before it is truncated behind a
// "See more" link (trigger CSV can easily overflow the column).
const TRIGGER_CSV_TRUNCATE = 30;

export const WEBHOOKS_TABLE_ENDPOINT = '/tables/webhooks';

// A trigger condition rendered as `type/action/status` (omitting absent parts).
const formatTrigger = ({ type, action, status }: WebhookTrigger): string =>
  [type, action, status].filter(Boolean).join('/');

// A single group (e.g. snapshot or intent-verification triggers) in the
// "See more" triggers dialog. Renders a count heading and the trigger list,
// or an empty-state message when the group has no triggers.
const TriggerGroup: FC<{
  label: string;
  emptyLabel: string;
  triggers: WebhookTrigger[];
}> = ({ label, emptyLabel, triggers }) => (
  <div>
    <h4 className='no-margin text-level-6 font-semibold'>{`${label} (${triggers.length})`}</h4>
    {triggers.length === 0 ? (
      <p className='mt-1 italic text-gray-500'>{emptyLabel}</p>
    ) : (
      <div className='mt-1 flex flex-col gap-1'>
        {triggers.map((trigger) => {
          const formatted = formatTrigger(trigger);
          return <div key={formatted}>{formatted}</div>;
        })}
      </div>
    )}
  </div>
);

// Row shape returned by POST /tables/webhooks. `sendAll`/`types`/`trigger` are
// hidden columns fetched to render the event-triggers cell.
interface WebHooksTableRow {
  id: WebHookId;
  active: boolean;
  username: string;
  name: string;
  sendAll: boolean;
  types: WebHookType[];
  trigger: WebhookTrigger[] | null;
}

interface Props {
  eventTypes: EventType[];
  msg: AppTranslations;
  // Silently refresh the webhooks array (which feeds the editor) after a list
  // mutation, so the side-panel editor doesn't show stale data.
  reloadWebHooks: () => void;
  testWebHook: (command: TestWebHookCommand) => void;
  webhooks: WebHookRowData[];
}

export const WebHooksList: FC<Props> = ({
  msg,
  webhooks,
  eventTypes,
  reloadWebHooks,
  testWebHook,
}) => {
  const navigate = useNavigate();
  const apiRequest = useApi().request;
  const { showNotification } = useNotifications();
  const getTypeTranslation = useTypeTranslator();
  const confirmDialog = useSimpleConfirmDialog();

  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testWebhook, setTestWebhook] = useState<WebHookRowData | null>(null);
  const [triggersDialog, setTriggersDialog] = useState<{
    name: string;
    triggers: WebhookTrigger[];
  } | null>(null);

  const apiScopeRequirementMet = useApiScopeRequirementMet();
  const canCreateWebHook = apiScopeRequirementMet({
    method: 'POST',
    path: '/webhooks',
  });
  const canEditWebHook = apiScopeRequirementMet({
    method: 'PUT',
    path: '/webhooks/:key',
  });
  const canDeleteWebHook = apiScopeRequirementMet({
    method: 'DELETE',
    path: '/webhooks/:key',
  });
  const canTestWebHook = apiScopeRequirementMet({
    method: 'POST',
    path: '/webhooks/:key/test/:type/:action',
  });

  // The full webhook objects (with url / templates / caCertId that the table
  // does not expose) are needed to rebuild the PUT body for the active toggle
  // and to seed the test dialog. Read them through a ref because the column
  // render closures are built once by `useTableEndpoint` and would otherwise
  // capture a stale array.
  const webhooksRef = useRef(webhooks);
  webhooksRef.current = webhooks;
  const findWebhook = (id: WebHookId): WebHookRowData | undefined =>
    webhooksRef.current.find((webhook) => webhook.id === id);

  // Guard against a double-toggle while a PUT is still in flight (the switch
  // reflects the row value, which only updates after the table reload).
  const togglingIds = useRef<Set<WebHookId>>(new Set());

  const handleToggleActive = useStableCallback(
    async (id: WebHookId, active: boolean) => {
      const webhook = findWebhook(id);
      if (!webhook || togglingIds.current.has(id)) {
        return;
      }
      togglingIds.current.add(id);
      try {
        // PUT /webhooks/{key} replaces the whole object (there is no PATCH), so
        // resend the persisted fields unchanged and omit `webhookConfig` to
        // preserve the stored outbound token.
        await apiRequest({
          url: `/webhooks/${id}`,
          method: 'PUT',
          data: {
            active,
            name: webhook.name,
            url: webhook.url,
            sendAll: webhook.sendAll,
            types: webhook.types,
            trigger: webhook.trigger,
            payloadTemplate: webhook.payloadTemplate ?? null,
            headerTemplate: webhook.headerTemplate ?? null,
            caCertId: webhook.caCertId,
          },
        });
        apiTablePage.apiTableModel.dispatch(reloadTableData);
        // Also resync the webhooks array so the editor reflects the new state.
        reloadWebHooks();
      } finally {
        togglingIds.current.delete(id);
      }
    },
  );

  const handleTest = useStableCallback((id: WebHookId) => {
    const webhook = findWebhook(id);
    if (!webhook) {
      return;
    }
    setTestWebhook(webhook);
    setShowTestDialog(true);
  });

  const deletionHelper = useApiTableDeletionHelper({
    confirmDialog,
    confirmDialogMessages: msg.settings.webHooks.deleteConfirmDialog,
    getApiTableModel: () => apiTablePage.apiTableModel,
    // Match the sibling row actions (plain IconButton, `small`) so the delete
    // icon lines up, and use the negative accent for the red trash can.
    renderDeleteButton: (id, handleDeleteClick) => (
      <Tooltip content={msg.settings.webHooks.btnDelete}>
        <IconButton
          variant='plain'
          accent='negative'
          size='small'
          icon='fa-trash-can'
          onClick={() => handleDeleteClick(id)}
          type='button'
          dataTestId='webhook-delete-button'
        />
      </Tooltip>
    ),
    // A single `deleteMultiple` (rather than `deleteOne`) is invoked exactly once
    // per operation for both single-row and bulk deletion, so the success toast
    // fires once instead of once per deleted row.
    deleteMultiple: async (ids: string[]) => {
      await Promise.all(
        ids.map((id) =>
          apiRequest({ url: `/webhooks/${id}`, method: 'DELETE' }),
        ),
      );
      showNotification({
        type: 'success',
        message: msg.settings.webHooks.request.deleted,
      });
    },
  });

  const endpoint = useTableEndpoint({
    apiEndpoint: WEBHOOKS_TABLE_ENDPOINT,
    alternative: {
      id: 'webhooks',
      title: '',
      columns: [
        createFrontendOnlyColumn<WebHooksTableRow>({
          key: 'active',
          title: msg.settings.webHooks.column.active,
          disabledSort: true,
          width: 81,
          fixedWidth: true,
          webDependencies: ['id', 'active'],
          className: 'text-center',
          render: (row) => (
            <div
              className='flex items-center justify-center'
              style={{ height: 'var(--table-row-height)' }}
            >
              <Switch
                value={row.active}
                disabled={!canEditWebHook}
                onChange={(next) => handleToggleActive(row.id, next)}
              />
            </div>
          ),
        }),
        'username',
        'name',
        createFrontendOnlyColumn<WebHooksTableRow>({
          key: 'eventTriggers',
          title: msg.settings.webHooks.column.eventTypes,
          disabledSort: true,
          webDependencies: ['sendAll', 'trigger', 'types'],
          csvStringDependencies: ['sendAll', 'trigger', 'types'],
          toCsvString: (row) =>
            row.sendAll
              ? msg.settings.webHooks.types.sendAll
              : row.trigger?.length
                ? row.trigger.map(formatTrigger).join(',')
                : row.types.map(getTypeTranslation).join(', '),
          render: (row) => {
            if (row.sendAll) {
              return msg.settings.webHooks.types.sendAll;
            }
            // Fine-grained webhooks: render the trigger triplets, truncated.
            if (row.trigger?.length) {
              const triggers = row.trigger.map(formatTrigger);
              const csv = triggers.join(',');
              if (csv.length <= TRIGGER_CSV_TRUNCATE) {
                return csv;
              }
              return (
                <span>
                  {`${csv.slice(0, TRIGGER_CSV_TRUNCATE)}… [`}
                  <ButtonAsA
                    onClick={() =>
                      setTriggersDialog({
                        name: row.name,
                        triggers: row.trigger ?? [],
                      })
                    }
                  >
                    {msg.settings.webHooks.seeMore}
                  </ButtonAsA>
                  {']'}
                </span>
              );
            }
            // Legacy webhooks: the selected event types.
            return row.types.map(getTypeTranslation).join(', ');
          },
        }),
        createFrontendOnlyColumn<WebHooksTableRow>({
          key: 'actions',
          title: msg.settings.webHooks.column.action,
          disabledSort: true,
          width: 116,
          fixedWidth: true,
          webDependencies: ['id'],
          render: (row) => (
            <div className='inline-flex gap-1'>
              {canEditWebHook && (
                <Tooltip content={msg.settings.webHooks.btnEdit}>
                  <IconButton
                    variant='plain'
                    accent='positive'
                    size='small'
                    icon='fa-pen'
                    onClick={() => navigate(`${WEBHOOKS_BASE_URL}/${row.id}`)}
                    type='button'
                    dataTestId='webhook-edit-button'
                  />
                </Tooltip>
              )}
              {canTestWebHook && (
                <Tooltip content={msg.settings.webHooks.btnTest}>
                  <IconButton
                    variant='plain'
                    size='small'
                    icon='fa-bolt'
                    onClick={() => handleTest(row.id)}
                    type='button'
                    dataTestId='webhook-test-button'
                  />
                </Tooltip>
              )}
              {canDeleteWebHook && deletionHelper.renderDeleteButton(row.id)}
            </div>
          ),
        }),
      ],
    },
  });

  const bulkActions = useMemo(
    (): TableBulkAction[] =>
      canDeleteWebHook ? [deletionHelper.deleteBulkAction] : [],
    [canDeleteWebHook, deletionHelper.deleteBulkAction],
  );

  const menuItems = useMemo(
    (): TableActionsItem[] =>
      canCreateWebHook
        ? [
            {
              standalone: true,
              elementWithKey: (
                <Button
                  key='add'
                  size='small'
                  variant='outline'
                  icon='fa-plus-circle'
                  onClick={() => navigate(`${WEBHOOKS_BASE_URL}/create`)}
                  type='button'
                >
                  {msg.settings.webHooks.btnAdd}
                </Button>
              ),
            },
          ]
        : [],
    [canCreateWebHook, msg],
  );

  const apiTablePage = useApiTablePage({
    endpoint,
    additionalApiTableProps: {
      rowIdField: 'id',
      bulkActions,
      loading: deletionHelper.loading,
      menuItems,
      // `id` is unique, so an explicit default sort gives a deterministic,
      // pagination-stable order (the SQL has no default ORDER BY otherwise).
      initialSort: { column: 'id', order: 'asc' },
    },
  });

  const triggerGroups: {
    type: WebHookType;
    label: string;
    emptyLabel: string;
  }[] = [
    {
      type: 'snapshot',
      label: msg.settings.webHooks.triggersDialog.snapshotEvents,
      emptyLabel: msg.settings.webHooks.triggersDialog.noSnapshotEvents,
    },
    {
      type: 'intent-verification',
      label: msg.settings.webHooks.triggersDialog.intentEvents,
      emptyLabel: msg.settings.webHooks.triggersDialog.noIntentEvents,
    },
  ];

  return (
    <>
      <ApiTablePageView
        model={apiTablePage}
        borderless
        tableViewClassName='-mx -mb mt flex-1 border-t border-default rounded-b'
      />
      {confirmDialog.render()}
      {showTestDialog && testWebhook && (
        <TestDialog
          eventTypes={eventTypes}
          onClose={() => setShowTestDialog(false)}
          testWebHook={testWebHook}
          webhook={testWebhook}
        />
      )}
      {triggersDialog && (
        <Dialog
          capToWindowHeight
          headerMsg={
            <span className='text-level-3'>
              {`${msg.settings.webHooks.triggersDialog.header} ${triggersDialog.name}`}
            </span>
          }
          primaryBtnMsg={msg.settings.webHooks.triggersDialog.btnClose}
          onPrimaryClick={() => setTriggersDialog(null)}
          onClose={() => setTriggersDialog(null)}
        >
          <div className='flex flex-col gap-4'>
            {triggerGroups.map(({ type, label, emptyLabel }) => (
              <TriggerGroup
                key={type}
                label={label}
                emptyLabel={emptyLabel}
                triggers={triggersDialog.triggers.filter(
                  (trigger) => trigger.type === type,
                )}
              />
            ))}
          </div>
        </Dialog>
      )}
    </>
  );
};
